import React, { useState, useMemo, useEffect, useContext, useRef } from 'react';
import WizardContentParent from './WizardContentParent';
import WizardProteinTreeDataSupplier from 'business/tree/WizardProteinTreeDataSupplier';
import ExperimentNameReview from './ExperimentNameReview';
import RMTable from 'components/customComponents/Controls/RMTable';
import ScreenWizardContext from './ScreenWizardContext';
import cloneDeep from 'lodash/cloneDeep';
import { ExperimentInfo, ScreenInfoDTO, StringPropertyDTO } from 'business/models/Models';
import EReviewExpNameOptions from 'enums/EReviewExpOptions';
import {
  getParentPath,
  getNewFolderType,
  getParents,
  isNullOrUndefined,
  MaxNameLength,
} from 'utils/Global';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import useRockFetchGet from 'utils/RockFetch';
import TableInput from './TableInput';
import LayersError from 'components/customComponents/Layers/LayersError';
import RMButton from 'components/customComponents/Controls/RMButton';
import TreeNodeCreator from '../tree/CreateNodeModal';
import { ECreateNodeType } from '../tree/ProjectNode';
import Explorer, { TreeControlDefaultState, TreeControlState } from 'components/explorer';
import { GlobalPreferencesService } from 'business/services/GlobalPreferencesService';
import { PropertyConstants } from 'enums/PropertyConstants';
import { useGetSelectValues } from './hooks';
import moment from 'moment';

const shouldRenderNode = (node) => {
  if (node.type === ETreeNodeTypes.RecycleBin) {
    return null;
  }
  return node;
};

let OverwriddenScreenExperimentNames = {};
function ReviewExperiment({
  screens,
  commonTreeNodes,
  gSelectedNode,
  selectedParentNode,
}: any) {
  const explorerRef = useRef(null);
  const [parentNode, setParentNode] = useState(gSelectedNode);

  const [duplicatedExpNames, setDuplicatedExpNames] = useState<string[]>([]);
  // containing experiment name that is too long
  const [invalidExpNames, setInvalidExpNames] = useState<string[]>([]);

  const [rockFetchGet] = useRockFetchGet();
  const screenExpWiz = useContext(ScreenWizardContext);
  const [tableData, setTableData] = useState([]);
  const [treeControlState, setTreeControlState] = useState<TreeControlState>({
    ...TreeControlDefaultState,
    selectedNodes: [`node${gSelectedNode}`],
  });
  const [createNodeType, setCreateNodeType] = useState(ECreateNodeType.None);

  const [defaultSettings, setDefaultSettings] = useState<{
    screenExpWizardNameDelimiter: string;
    screenExpWizardDateTimeFormat: string;
  }>({
    screenExpWizardNameDelimiter: '',
    screenExpWizardDateTimeFormat: '',
  });

  useEffect(() => {
    const fetchProperties = async () => {
      const globalPreferencesClient = new GlobalPreferencesService();

      const [defaultDelimiter, defaultDateTimeFormat] = await Promise.all([
        globalPreferencesClient.getProperty(
          PropertyConstants.ScreenExperimentWizardNameDelimiter,
        ),
        globalPreferencesClient.getProperty(
          PropertyConstants.ScreenExperimentWizardDateTimeFormat,
        ),
      ]);

      setDefaultSettings({
        screenExpWizardNameDelimiter: (defaultDelimiter as StringPropertyDTO).value,
        screenExpWizardDateTimeFormat: (defaultDateTimeFormat as StringPropertyDTO).value,
      });
    };
    OverwriddenScreenExperimentNames = {};
    fetchProperties();
  }, []);

  useEffect(() => {
    if (duplicatedExpNames.length > 0 || invalidExpNames.length > 0) {
      screenExpWiz.setExpNameError(true);
    } else {
      screenExpWiz.setExpNameError(false);
    }
  }, [duplicatedExpNames, invalidExpNames]);

  const dateToday = moment().format(
    defaultSettings.screenExpWizardDateTimeFormat.toUpperCase() || 'DD-MMM-YY',
  );
  const dataArray = [];

  const [prefixInput, setPrefixInput] = useState(screenExpWiz.expNamePrefix || '');
  const [postfixInput, setPostfixInput] = useState(screenExpWiz.expNameSuffix || '');
  const [prefixCheckbox, setPrefixCheckbox] = useState(
    !screenExpWiz.createNewExp && !!screenExpWiz.expNamePrefix,
  );
  const [postfixCheckbox, setPostfixCheckbox] = useState(
    !screenExpWiz.createNewExp && !!screenExpWiz.expNameSuffix,
  );
  const [childNodes, setChildNodes] = useState<string[]>([]);
  let tempChildNodes = [];
  const { firstValue, secondValue, thirdValue } = useGetSelectValues(
    screenExpWiz.expNameFormatString,
  );
  const [secondSelectValue, setSecondSelectValue] = useState(secondValue);
  const [firstSelectValue, setFirstSelectValue] = useState(firstValue);
  const [thirdSelectValue, setThirdSelectValue] = useState(thirdValue);

  const parsedValues = useMemo(() => {
    const rawValues =
      screenExpWiz.expNameFormatString?.match(/<([^>]+)>/g)?.map((v) => v.slice(1, -1)) ||
      [];
    return {
      hasFirst: rawValues.length >= 1,
      hasSecond: rawValues.length >= 2,
      hasThird: rawValues.length >= 3,
    };
  }, [screenExpWiz.expNameFormatString]);

  const [secondCheckbox, setSecondCheckbox] = useState(parsedValues.hasSecond);
  const [thirdCheckbox, setThirdCheckbox] = useState(parsedValues.hasThird);

  function setTemplateDataDirty() {
    !screenExpWiz.createNewExp && screenExpWiz.setWizardDataDirty(true);
  }

  function changePrefixInputValue(e) {
    const textVal = e.target.value;
    setPrefixInput(textVal);
    screenExpWiz.setExpNamePrefix(textVal);
    setTemplateDataDirty();
  }

  function getTempName(tempId) {
    const temp = screenExpWiz.allTemps.find((temp) => temp.id == tempId);
    return temp?.name;
  }

  function changePostfixInputValue(e) {
    const textVal = e.target.value;
    setPostfixInput(textVal);
    screenExpWiz.setExpNameSuffix(textVal);
    setTemplateDataDirty();
  }
  function changePrefixCheckboxValue(e) {
    const flValue = e.target.checked;
    setPrefixCheckbox(flValue);
  }
  function changePostfixCheckboxValue(e) {
    const flValue = e.target.checked;
    setPostfixCheckbox(flValue);
    setTemplateDataDirty();
  }
  function changeDateCheckbox(e) {
    const flValue = e.target.checked;
    setSecondCheckbox(flValue);
    setTemplateDataDirty();
  }
  function changeThirdCheckbox(e) {
    const flValue = e.target.checked;
    setThirdCheckbox(flValue);
    setTemplateDataDirty();
  }
  function changeSecondSelect(e) {
    const numVal = parseInt(e.target.value);
    setSecondSelectValue(numVal);
    setTemplateDataDirty();
  }
  function changeThirdSelect(e) {
    const numVal = parseInt(e.target.value);
    setThirdSelectValue(numVal);
    setTemplateDataDirty();
  }
  function changeFirstSelect(e) {
    const numVal = parseInt(e.target.value);
    setFirstSelectValue(numVal);
    setTemplateDataDirty();
  }
  const getDefaultDelimitedName = (name1, name2) => {
    if (isNullOrUndefined(name1) || isNullOrUndefined(name2)) {
      return;
    }
    if (name1.length === 0 && name2.length === 0) {
      return '';
    }
    if (name1.length === 0) {
      return name2;
    }
    if (name2.length === 0) {
      return name1;
    }
    return `${name1}${defaultSettings.screenExpWizardNameDelimiter}${name2}`;
  };
  function getExperimentName(screenName, temp) {
    let expName = '';
    let templateFormat = '';
    if (prefixCheckbox) {
      expName = getDefaultDelimitedName(expName, prefixInput);
    }
    switch (firstSelectValue) {
      case EReviewExpNameOptions.SCREENNAME:
        expName = getDefaultDelimitedName(expName, screenName);
        templateFormat = templateFormat + '<Screen>';
        break;
      case EReviewExpNameOptions.DATE:
        expName = getDefaultDelimitedName(expName, dateToday);
        templateFormat = templateFormat + '<Date>';
        break;
      case EReviewExpNameOptions.TEMPERATURE:
        expName = getDefaultDelimitedName(expName, `${temp}C`);
        templateFormat = templateFormat + '<Temperature>';
        break;
      default:
        break;
    }
    if (secondCheckbox) {
      switch (secondSelectValue) {
        case EReviewExpNameOptions.SCREENNAME:
          if (firstSelectValue != EReviewExpNameOptions.SCREENNAME) {
            expName = getDefaultDelimitedName(expName, screenName);
            templateFormat = templateFormat + '<Screen>';
          }
          break;
        case EReviewExpNameOptions.DATE:
          if (firstSelectValue != EReviewExpNameOptions.DATE) {
            expName = getDefaultDelimitedName(expName, dateToday);
            templateFormat = templateFormat + '<Date>';
          }
          break;
        case EReviewExpNameOptions.TEMPERATURE:
          if (firstSelectValue != EReviewExpNameOptions.TEMPERATURE) {
            expName = getDefaultDelimitedName(expName, `${temp}C`);
            templateFormat = templateFormat + '<Temperature>';
          }
          break;
        default:
          break;
      }
    }

    if (thirdCheckbox) {
      switch (thirdSelectValue) {
        case EReviewExpNameOptions.SCREENNAME:
          if (
            firstSelectValue != EReviewExpNameOptions.SCREENNAME &&
            secondSelectValue != EReviewExpNameOptions.SCREENNAME
          ) {
            expName = getDefaultDelimitedName(expName, screenName);
            templateFormat = templateFormat + '<Screen>';
          }
          break;
        case EReviewExpNameOptions.DATE:
          if (
            firstSelectValue != EReviewExpNameOptions.DATE &&
            secondSelectValue != EReviewExpNameOptions.DATE
          ) {
            templateFormat = templateFormat + '<Date>';
            expName = getDefaultDelimitedName(expName, dateToday);
          }
          break;
        case EReviewExpNameOptions.TEMPERATURE:
          if (
            firstSelectValue != EReviewExpNameOptions.TEMPERATURE &&
            secondSelectValue != EReviewExpNameOptions.TEMPERATURE
          ) {
            expName = getDefaultDelimitedName(expName, `${temp}C`);
            templateFormat = templateFormat + '<Temperature>';
          }
          break;
        default:
          break;
      }
    }
    screenExpWiz.setExpNameFormatString(templateFormat);
    if (postfixCheckbox) {
      expName = getDefaultDelimitedName(expName, postfixInput);
    }
    return expName;
  }
  const columns = useMemo(
    () => [
      {
        Header: 'No',
        accessor: 'srNo',
        className: 'alignContent reviewExpHeader1',
      },
      {
        Header: 'Screen Experiment Name',
        accessor: 'expName',
        className: 'alignContent',
      },
      {
        Header: 'Screen Name',
        accessor: 'screenName',
        className: 'alignContent',
      },
      {
        Header: 'Layer Vol(µL)',
        accessor: 'layerVol',
        className: 'alignContent reviewExpHeader2',
      },
      {
        Header: 'Incubation Temp',
        accessor: 'incubationTemp',
        className: 'alignContent reviewExpHeader2',
      },
    ],
    [],
  );
  function FindNameInList(nodes, name: string) {
    for (const node of nodes) {
      if (node.toLowerCase() == name.toLowerCase()) {
        return true;
      }
    }
    return false;
  }

  function verifyNames(expInfoList: ExperimentInfo[]) {
    duplicatedExpNames.splice(0, duplicatedExpNames.length);
    const tmpInvalidNames = [],
      tmpDuplicatedNames = [];
    const childNames = [...childNodes];
    expInfoList.forEach((expInfo) => {
      const requiredName = childNames.find(
        (expName) => expName === expInfo.screenExperimentName,
      );
      if (!isNullOrUndefined(requiredName)) {
        tmpDuplicatedNames.push(requiredName);
      } else if (expInfo.screenExperimentName.length > MaxNameLength.TREE_NODE) {
        tmpInvalidNames.push(expInfo.screenExperimentName);
      } else {
        childNames.push(expInfo.screenExperimentName);
      }
      setInvalidExpNames([...tmpInvalidNames]);
      setDuplicatedExpNames([...tmpDuplicatedNames]);
    });
  }
  function GetUniqueName(nodes, baseName = 'New Experiment') {
    let proposedName = baseName;
    for (let i = 1; ; i++) {
      if (!FindNameInList(nodes, proposedName)) {
        nodes.push(proposedName);
        return proposedName;
      } else {
        proposedName = baseName + ' (' + i + ')';
      }
    }
  }
  useEffect(() => {
    if (isNullOrUndefined(parentNode)) {
      return;
    }
    if (
      parentNode.type == ETreeNodeTypes.ProjectsFolder ||
      parentNode.type == ETreeNodeTypes.Projects
    ) {
      screenExpWiz.setExpSaveLocation(null);
      screenExpWiz.setCreateExpPath('');
    } else if (
      parentNode.type == ETreeNodeTypes.Project ||
      parentNode.type == ETreeNodeTypes.ProjectFolder
    ) {
      screenExpWiz.setExpSaveLocation(parentNode.id);
      const parentNodes = getParents(treeControlState.treenodes, parentNode.id);
      const expPath = getParentPath(parentNodes);
      screenExpWiz.setCreateExpPath(expPath);
    }
  }, [parentNode]);

  useEffect(() => {
    async function getChildNodes() {
      if (screenExpWiz.expSaveLocation == null) {
        return;
      }
      const root = await rockFetchGet(
        `/api/Tree/${screenExpWiz.expSaveLocation}`,
        'get',
        null,
        true,
      );
      if (root == null) {
        return;
      }

      const data = [];

      for (let i = 0; i < root.treeNodes.length; i++) {
        data.push(root.treeNodes[i].name);
      }
      setChildNodes(data);
    }
    getChildNodes();
  }, [screenExpWiz.expSaveLocation]);
  useEffect(() => {
    let iterator = 1;
    tempChildNodes = cloneDeep(childNodes);
    screenExpWiz.experimentInfoList.splice(0, screenExpWiz.experimentInfoList.length);

    screenExpWiz.screens.forEach((screen: ScreenInfoDTO) =>
      screenExpWiz.incubationTemp.forEach((temp) => {
        const tempName = getTempName(temp);
        const expName = getExperimentName(screen.name, tempName);
        function getUniqueExpName() {
          return GetUniqueName(tempChildNodes, expName);
        }
        function changeExpName(val: string) {
          OverwriddenScreenExperimentNames = {
            ...OverwriddenScreenExperimentNames,
            [screen.id]: val,
          };
          experimentInfo.screenExperimentName = val;
        }
        const uniqueExpName =
          OverwriddenScreenExperimentNames[screen.id] || getUniqueExpName();
        function onBlurExpName(e) {
          const val = e.target.value;
          if (val.length > 0) {
            verifyNames(screenExpWiz.experimentInfoList);
          }
        }
        dataArray.push({
          srNo: iterator,
          expName: (
            <TableInput
              initialValue={uniqueExpName}
              changeInputValue={changeExpName}
              type="textarea"
              onBlurInput={onBlurExpName}
            />
          ),
          templateName: expName,
          screenName: screen.name,
          layerVol: screen.layerVolume,
          incubationTemp: tempName,
        });
        const experimentInfo = new ExperimentInfo();
        experimentInfo.imagingScheduleID = screenExpWiz.imagingSchedule?.id;
        const plateType = screenExpWiz.plateType;
        experimentInfo.experimentContainerID = plateType.id;
        experimentInfo.screenInfo = screen;
        experimentInfo.imagingScheduleID = screenExpWiz.imagingSchedule.id;
        experimentInfo.screenExperimentName = uniqueExpName;
        experimentInfo.incubationTempID = temp;
        experimentInfo.setupTempID = temp;
        experimentInfo.experimentContainerID = screenExpWiz.plateType?.id;
        experimentInfo.ownerUserID = screenExpWiz.owner.id;
        screenExpWiz.experimentInfoList.push(experimentInfo);
        iterator++;
      }),
    );
    setTableData(dataArray);
    verifyNames(screenExpWiz.experimentInfoList);
  }, [
    screens,
    prefixCheckbox,
    prefixInput,
    postfixInput,
    firstSelectValue,
    secondSelectValue,
    postfixCheckbox,
    secondCheckbox,
    thirdCheckbox,
    thirdSelectValue,
    childNodes,
  ]);

  async function newNode(newNode, parentTreeNodeId) {
    explorerRef?.current?.newNode(newNode, [], parentTreeNodeId);
  }
  function createFolderClicked(nodeType) {
    setCreateNodeType(nodeType);
  }

  function getParentNodeVal() {
    const isNull = isNullOrUndefined(parentNode);
    return isNull;
  }
  return (
    <>
      <div id="reviewexp-container" className="flex h-[90%]">
        <WizardContentParent
          id="reviewexp-left-wizardcontentparent"
          style={{ flex: '3', marginLeft: '25px' }}
          heading="Select Location for the Experiment(s)"
          childStyle={{ paddingBottom: '8%' }}
        >
          <div id="reviewexp-explorer-wrapper" style={{ height: '90%' }}>
            <Explorer
              ref={explorerRef}
              setParentNode={setParentNode}
              showQuickAccessNode={false}
              treeControlState={treeControlState}
              setTreeControlState={setTreeControlState}
              dataSupplier={WizardProteinTreeDataSupplier}
              shouldRenderNode={shouldRenderNode}
              treeNodeIdToLoadTree={selectedParentNode?.id}
            />
          </div>
          <div
            id="reviewexp-left-button-container"
            style={{ height: '10%' }}
            className="flex flex-xy-center flex-space-between"
          >
            <RMButton
              id="reviewexp-btn-newfolder"
              customStyle={{ width: '45%', background: '#666666' }}
              onClick={(e) => {
                createFolderClicked(ECreateNodeType.ProjectFolder);
              }}
              disabled={getParentNodeVal()}
              label="New Folder"
            ></RMButton>
            <RMButton
              id="reviewexp-btn-newproject"
              customStyle={{ width: '45%', background: '#666666' }}
              onClick={(e) => {
                createFolderClicked(ECreateNodeType.Project);
              }}
              disabled={
                getParentNodeVal() ||
                (parentNode?.type != ETreeNodeTypes.ProjectsFolder &&
                  parentNode?.type != ETreeNodeTypes.Projects)
              }
              label="New Project"
            ></RMButton>
          </div>
        </WizardContentParent>
        <WizardContentParent
          id="reviewexp-right-wizardcontentparent"
          heading="Experiment Name Review"
          style={{ marginLeft: '22px', flex: '7', padding: '0px' }}
          headingStyle={{ marginLeft: '15px' }}
        >
          <ExperimentNameReview
            id="reviewexp-experimentnamereview"
            prefixInputVal={prefixInput}
            postfixInputVal={postfixInput}
            changePrefixCheckbox={changePrefixCheckboxValue}
            changePostfixCheckbox={changePostfixCheckboxValue}
            changePrefixInputVal={changePrefixInputValue}
            changePostfixInputVal={changePostfixInputValue}
            dateCheckboxValue={secondCheckbox}
            changeDateCheckbox={changeDateCheckbox}
            secondSelectValue={secondSelectValue}
            changeDateSelectValue={changeSecondSelect}
            firstSelectValue={firstSelectValue}
            changeScreenSelectValue={changeFirstSelect}
            prefixCheckboxVal={prefixCheckbox}
            postfixCheckboxVal={postfixCheckbox}
            thirdCheckbox={thirdCheckbox}
            thirdSelectValue={thirdSelectValue}
            changeThirdSelectValue={changeThirdSelect}
            changeThirdCheckbox={changeThirdCheckbox}
          />
          <p id="reviewexp-customize-text" className="review-exp-text">
            You may customize the names by clicking on the name of the experiment and
            editing the text
          </p>
          <div
            id="reviewexp-table-container"
            className="screen-wizard-table wizard-review-experiment-table"
            style={{ overflow: 'auto', height: '74%' }}
          >
            <div id="reviewexp-table-wrapper" style={{ display: 'flex', height: '90%' }}>
              {screenExpWiz.expSaveLocation != null && (
                <RMTable tableClass="w-full h-full" columns={columns} data={tableData} />
              )}
            </div>
            <div id="reviewexp-errors" style={{ height: '10%' }}>
              {invalidExpNames.map((expName, index) => {
                const name =
                  expName.length > MaxNameLength.TREE_NODE
                    ? `${expName.slice(0, MaxNameLength.TREE_NODE)}...`
                    : expName;
                return (
                  <LayersError
                    key={`layer_error_invalid${index}`}
                    errorText={`Experiment name is too long: ${name}`}
                  />
                );
              })}
              {duplicatedExpNames.map((expName, index) => (
                <LayersError
                  key={`layer_error_duplicated${index}`}
                  errorText={`${expName}- already exist`}
                />
              ))}
            </div>
          </div>
        </WizardContentParent>
      </div>
      <TreeNodeCreator
        type={createNodeType}
        setType={setCreateNodeType}
        parentTreeNodeId={treeControlState.selectedNodes[0]?.id}
        newNode={newNode}
        newFolderType={getNewFolderType(
          treeControlState.treenodes,
          treeControlState.selectedNodes[0],
        )}
      />
    </>
  );
}

export default ReviewExperiment;
